import { useEffect, useState } from 'react';

const DISMISS_KEY = 'omnix_install_prompt_dismissed_at';
const DISMISS_DAYS = 14;

const isDismissed = () => {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY));
    return ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const rememberDismissal = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode — ignore */
  }
};

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPhone|iPad|iPod/i.test(window.navigator.userAgent);

/**
 * InstallPrompt — dismissible "add to home screen" banner.
 * - Chromium: stashes the `beforeinstallprompt` event and offers an Install
 *   button that triggers the native prompt.
 * - iOS Safari (no beforeinstallprompt): shows a one-time hint to use
 *   Share → Add to Home Screen.
 * Dismissal is remembered in localStorage for 14 days.
 */
const InstallPrompt = () => {
  const [installEvent, setInstallEvent] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return undefined;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      setVisible(true);
    };
    const onAppInstalled = () => {
      setVisible(false);
      setInstallEvent(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    // iOS never fires beforeinstallprompt — show a manual hint instead
    if (isIOS() && !window.navigator.standalone) {
      setShowIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    rememberDismissal();
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === 'dismissed') rememberDismissal();
    setInstallEvent(null);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Install OmniConnX"
      className="fixed bottom-20 md:bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[100] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border bg-white border-slate-200 text-slate-800 dark:bg-dark-800 dark:border-dark-700 dark:text-slate-100"
    >
      <img
        src="/OmniConnX_Web_Brand_Package/favicons/favicon-48x48.png"
        alt=""
        className="w-9 h-9 rounded-lg shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">Install OmniConnX</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {showIosHint && !installEvent
            ? 'Install: Share → Add to Home Screen'
            : 'Add it to your home screen'}
        </p>
      </div>
      {installEvent && (
        <button
          type="button"
          onClick={install}
          className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:opacity-90 transition-opacity shrink-0"
        >
          Install
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors shrink-0"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default InstallPrompt;
